import { AxiosInterceptorManager } from "axios";

export class InterceptorManager<V> implements AxiosInterceptorManager<V> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handlers: Array<Record<"fulfilled" | "rejected", any> | null> = [];
  private _handlerIds: number[] = [];

  /**
   * Add a new interceptor to the stack
   *
   * @param {Function} fulfilled The function to handle `then` for a `Promise`
   * @param {Function} rejected The function to handle `reject` for a `Promise`
   *
   * @return {Number} An ID used to remove interceptor later
   */
  use<T = V>(
    fulfilled: (value: V) => T | Promise<T>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rejected: (error: any) => any
  ): number {
    const id =
      this.handlers.length === 0
        ? 100
        : this._handlerIds[this.handlers.length - 1] + 1;
    this._handlerIds.push(id);
    this.handlers.push({ fulfilled: fulfilled, rejected: rejected });
    return id;
  }

  /**
   * Add a new interceptor to the stack
   *
   * @param {Function} fulfilled The function to handle `then` for a `Promise`
   * @param {Function} rejected The function to handle `reject` for a `Promise`
   *
   * @return {Number} An ID used to remove interceptor later
   */
  unshift<T = V>(
    fulfilled: (value: V) => T | Promise<T>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rejected: (error: any) => any
  ): number {
    const id = this.handlers.length === 0 ? 100 : this._handlerIds[0] - 1;
    this._handlerIds.unshift(id);
    this.handlers.unshift({ fulfilled: fulfilled, rejected: rejected });
    return id;
  }

  /**
   * Remove an interceptor from the stack
   *
   * @param {Number} id The ID that was returned by `use`
   */
  eject(id: number) {
    const handlerIndex = this._handlerIds.indexOf(id);
    if (this.handlers[handlerIndex]) {
      this.handlers[id] = null;
    }
  }

  /**
   * Return all interceptors
   */
  getAll() {
    return this.handlers
      .filter((interceptor) => interceptor !== null)
      .map((interceptor) => {
        return { ...interceptor };
      });
  }

  /**
   * Flush all interceptors
   */
  flush() {
    this.handlers.forEach(
      (interceptor, index) => (this.handlers[index] = null)
    );
  }
}
